import { translate } from '../i18n/i18n';
import { token as liveToken } from '../auth/session';

const BASE = '/api';

class HttpError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export type RequestOpts = {
  method?: string;
  /**
   * Whether this call is an authenticated one. Any truthy value means yes.
   *
   * THE VALUE PASSED IS NOT THE VALUE SENT, deliberately. A realm access token
   * lasts five minutes, so a token read into a component when it rendered is
   * routinely stale by the time a button is pressed. This asks the session for
   * a live one at the moment of the call, refreshing it if needed.
   */
  token?: string | null | boolean;
  body?: unknown;
  signal?: AbortSignal;
};

let unauthorizedHandler: (() => void) | null = null;

/**
 * Register a callback fired when an authenticated request is rejected (401 or 403).
 * AuthProvider uses this to clear the stale session and bounce the user to the login screen.
 */
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  unauthorizedHandler = fn;
}

const ACTING_KEY = 'easylearning.assist.acting';

export type ActingFor = { engagementId: string; family: string; learners: string[] };

export function actingFor(): ActingFor | null {
  try {
    const raw = sessionStorage.getItem(ACTING_KEY);
    return raw ? (JSON.parse(raw) as ActingFor) : null;
  } catch {
    return null;
  }
}

export function setActingFor(a: ActingFor | null) {
  try {
    if (a) sessionStorage.setItem(ACTING_KEY, JSON.stringify(a));
    else sessionStorage.removeItem(ACTING_KEY);
  } catch {
    return;
  }
}

export function actingHeader(path: string): Record<string, string> {
  const a = actingFor();
  return a && !path.startsWith('/assist') ? { 'X-Assist-Engagement': a.engagementId } : {};
}

export async function api<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json', ...actingHeader(path) };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.token) {
    const fresh = await liveToken();
    if (fresh) headers.Authorization = `Bearer ${fresh}`;
  }

  const res = await fetch(`${BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
    credentials: 'omit',
  });

  if (res.status === 204) return undefined as T;

  let parsed: unknown = null;
  const text = await res.text();
  if (text) {
    try { parsed = JSON.parse(text); } catch { parsed = text; }
  }

  if (!res.ok) {
    if (res.status === 410 && headers['X-Assist-Engagement']) {
      setActingFor(null);
      window.location.assign('/assist/expert');
    }
    if (res.status === 401 || res.status === 403) {
      unauthorizedHandler?.();
    }
    const msg =
      (parsed && typeof parsed === 'object' && 'message' in (parsed as Record<string, unknown>) &&
        typeof (parsed as { message: unknown }).message === 'string')
        ? translate((parsed as { message: string }).message)
        : translate('HTTP {status}', { status: res.status });
    throw new HttpError(res.status, msg, parsed);
  }
  return parsed as T;
}

export { HttpError };
